import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { Artwork } from "../types";
import { STATUS_LABEL } from "../types";
import { artImage } from "../utils/image";
import { tokens } from "../theme";

export default function ArtworkCard({
  art,
  onOpen,
}: {
  art: Artwork;
  onOpen: (a: Artwork) => void;
}) {
  const sold = art.status === "sold";
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(art)}
      onKeyDown={(e) =>
        (e.key === "Enter" || e.key === " ") &&
        (e.preventDefault(), onOpen(art))
      }
      sx={{
        cursor: "pointer",
        display: "block",
        outline: "none",
        "&:focus-visible .tl-frame": { boxShadow: `0 0 0 2px ${tokens.brass}` },
        "&:hover .tl-img": { transform: "scale(1.045)" },
        "&:hover .tl-rule": { width: "100%" },
        "&:hover .tl-title": { color: tokens.accent },
      }}
    >
      <Box
        className="tl-frame"
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: tokens.paper,
          border: `1px solid ${tokens.line}`,
          boxShadow: "0 1px 2px rgba(27,26,23,0.04)",
          transition: "box-shadow 200ms ease",
        }}
      >
        <Box
          component="img"
          className="tl-img"
          src={artImage(art)}
          alt={art.title}
          loading="lazy"
          sx={{
            width: "100%",
            height: "auto",
            display: "block",
            transition: "transform 700ms cubic-bezier(.2,.7,.2,1)",
          }}
        />
        {(sold || art.status === "nfs") && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              px: 1.2,
              py: 0.4,
              bgcolor: "rgba(27,26,23,0.86)",
              color: tokens.bone,
              fontSize: "0.62rem",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
            }}
          >
            {STATUS_LABEL[art.status]}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          pt: 1.4,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            className="tl-title"
            sx={{
              fontFamily: tokens.display,
              fontSize: "1.32rem",
              color: tokens.ink,
              lineHeight: 1.15,
              transition: "color 180ms ease",
            }}
          >
            {art.title}
          </Typography>
          <Box
            className="tl-rule"
            sx={{
              height: "1px",
              width: 22,
              bgcolor: tokens.brass,
              my: 0.7,
              transition: "width 320ms ease",
            }}
          />
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: tokens.taupe,
              letterSpacing: "0.02em",
            }}
          >
            {[art.medium, art.dimensions, art.galleryAttribution]
              .filter(Boolean)
              .join("  ·  ")}
          </Typography>
        </Box>
        <Typography
          sx={{
            whiteSpace: "nowrap",
            fontSize: "0.82rem",
            fontWeight: 600,
            color: sold ? tokens.taupe : tokens.ink,
            mt: 0.4,
          }}
        >
          {art.status === "available" ? (art.price ?? "") : ""}
        </Typography>
      </Box>
    </Box>
  );
}
